import { useEffect } from 'react';
import { useMutation } from '@apollo/client';
import { Alert, Stack } from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import {
  GET_FIND_GROUP_QUERY,
  GET_MY_FIND_GROUPS_QUERY,
  UPDATE_FIND_GROUP_MUTATION,
} from '../../gql';
import { FindGroupRow } from './FindGroupsTable';
import { FormattedMessage } from 'react-intl';
import {
  Button,
  FieldLabel,
  FormField,
  SurfaceTextField,
} from '../../components/design-system';

const containerStyles = { maxWidth: 640, flexGrow: 1 };
const inputStyles = { width: '100%' };

type FormValues = { name: string; description: string };

export function FindGroupSettings({ group }: { group: FindGroupRow }) {
  const defaultValues = {
    name: group.name ?? '',
    description: group.description ?? '',
  };
  const { control, handleSubmit, reset, formState } = useForm<FormValues>({
    defaultValues,
  });
  const [updateGroup, { loading }] = useMutation(UPDATE_FIND_GROUP_MUTATION, {
    refetchQueries: [
      { query: GET_FIND_GROUP_QUERY, variables: { id: group.id } },
      { query: GET_MY_FIND_GROUPS_QUERY },
    ],
  });
  const readOnly = Boolean(group.shared);
  const ownerLabel =
    group.createdByName?.trim() || group.createdByEmail || group.createdBy;

  useEffect(() => {
    reset(defaultValues);
  }, [group.description, group.name, reset]);

  const onSubmit = async (values: FormValues) => {
    await updateGroup({
      variables: {
        id: group.id,
        group: {
          name: values.name || null,
          description: values.description || null,
        },
      },
    });
    reset(values);
  };

  return (
    <Stack
      component="form"
      gap={2}
      sx={containerStyles}
      onSubmit={handleSubmit(onSubmit)}
    >
      {readOnly && (
        <Alert severity="info">
          <FormattedMessage
            id="finds.group.sharedBy"
            defaultMessage="This group is shared with you by {owner}."
            values={{ owner: ownerLabel }}
          />
        </Alert>
      )}
      <FormField>
        <FieldLabel htmlFor="find-group-name">
          <FormattedMessage id="finds.group.name" defaultMessage="Group name" />
        </FieldLabel>
        <Controller
          name="name"
          control={control}
          disabled={readOnly}
          render={({ field }) => (
            <SurfaceTextField
              id="find-group-name"
              surface="sunken"
              sx={inputStyles}
              {...field}
            />
          )}
        />
      </FormField>
      <FormField>
        <FieldLabel htmlFor="find-group-description">
          <FormattedMessage
            id="form.description"
            defaultMessage="Description"
          />
        </FieldLabel>
        <Controller
          name="description"
          control={control}
          disabled={readOnly}
          render={({ field }) => (
            <SurfaceTextField
              id="find-group-description"
              multiline
              minRows={2}
              maxRows={5}
              surface="sunken"
              sx={inputStyles}
              {...field}
            />
          )}
        />
      </FormField>
      {!readOnly && (
        <Stack direction="row" justifyContent="flex-end">
          <Button type="submit" disabled={!formState.isDirty || loading}>
            <FormattedMessage id="action.save" defaultMessage="Save" />
          </Button>
        </Stack>
      )}
    </Stack>
  );
}
